import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, ModuleFE_Toaster, openContent, SimpleListAdapter, TS_DropDown, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { TS_InputV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_Input/TS_InputV2';
import { RefSystemToReferenceType, UnresolvedCodeStatuses, UnresolvedCodeStatus_Rejected, UnresolvedCodeStatus_WaitingForUserDecision, UnresolvedCodeStatus_NewAndWaitingForNLP, UnresolvedCodeStatus_NewHVCreated } from '@app/runtime-tools-shared';
import { ConfigKeyFE_SourceTagId, Dialog_VariableEditor, ModuleFE_Variable, ReactiveDropDown_VariableV3WithSynonyms } from '@app/hcs-frontend';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { ModuleFE_Artifact } from '@app/org-frontend';
import { StatusLabels } from './status-labels.js';
import './Editor_UnresolvedCode.scss';
import { ICONS } from '@app/styles';
const ViewKey_View = 'view';
const ViewKey_Edit = 'edit';
export class Editor_UnresolvedCode extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        state.editMode = nextProps.editMode;
        return state;
    }
    // ######################## Logic ########################
    getViewMode = () => this.state.editMode ? ViewKey_Edit : ViewKey_View;
    onInputChange = (prop, value) => {
        this.state.editable.update(prop, value);
    };
    saveItem = async () => {
        try {
            await this.state.editable.save();
        }
        catch (e) {
            console.error('Failed to save item:', e);
            ModuleFE_Toaster.toastError('Failed to save item');
        }
    };
    onClickDone = async () => {
        try {
            this.props.onEditModeChange(false);
        }
        catch (e) {
            console.error('Failed set edit mode:', e);
        }
    };
    onClickReject = async () => {
        try {
            this.state.editable.update('status', UnresolvedCodeStatus_Rejected);
            await this.saveItem();
        }
        catch (e) {
            ModuleFE_Toaster.toastError('Failed to reject item');
        }
    };
    onSelectVariable = async (id) => {
        await this.state.editable.updateObj({ chosenHvId: id });
        this.forceUpdate();
    };
    openVariableEditor = (variableId) => {
        const { editable } = this.state;
        const tagId = ConfigKeyFE_SourceTagId.get();
        // open an existing variable
        if (variableId) {
            const variable = ModuleFE_Variable.cache.unique(variableId);
            if (!variable) {
                ModuleFE_Toaster.toastError('Variable not found, can not open editor.');
                return;
            }
            Dialog_VariableEditor.show(tagId, variable);
        }
        else {
            // open a new variable with callback to auto-select when created and set status to new_hv_created
            const refSystem = editable.item.refSystem;
            const refType = refSystem ? RefSystemToReferenceType[refSystem] : undefined;
            const refCode = editable.item.code;
            const initialRef = refType && refCode ? { refType, refCode } : undefined;
            Dialog_VariableEditor.show(tagId, undefined, initialRef, async (savedVariable) => {
                await this.onSelectVariable(savedVariable._id);
                this.state.editable.update('status', UnresolvedCodeStatus_NewHVCreated);
                try {
                    await this.state.editable.save();
                }
                catch (e) {
                    this.logError('Failed to save after creating variable', e);
                    ModuleFE_Toaster.toastError('Failed to save unresolved code status');
                }
            });
        }
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__unresolved-code', children: [this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        const { editMode, editable } = this.state;
        return _jsxs(LL_H_C, { className: 'editor__header', children: [_jsxs(LL_V_L, { className: 'editor__header__info', children: [_jsx("span", { className: 'editor__header__title', children: "Unresolved Code" }), _jsx("span", { className: 'editor__header__subtitle', children: editable.item.code })] }), _jsx(LL_H_C, { className: 'editor__header__actions', children: editMode ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'secondary', onClick: this.saveItem, children: "Save" }), _jsx(Button, { variant: 'primary', onClick: this.onClickDone, children: "Done" })] })) : (_jsx(Button, { variant: 'secondary', onClick: () => this.props.onEditModeChange(true), children: "Edit" })) })] });
    };
    renderBody = () => {
        return _jsxs(LL_V_L, { className: 'editor__unresolved-code__body', children: [_jsxs(LL_V_L, { className: 'editor__section', children: [_jsx("span", { className: 'editor__section__title', children: "Data from fhir" }), _jsxs(LL_V_L, { className: 'editor__section__content', children: [this.renderEditor_Text(), this.renderEditor_Display(), this.renderEditor_Code(), this.renderEditor_RefSystem()] })] }), this.renderEditor_Status(), this.renderEditor_SuggestedHvIds(), this.renderEditor_ChosenHvId(), this.renderEditor_FhirQueryLabel(), this.renderEditor_KbVersion(), this.renderRejectButton()] });
    };
    // ######################## Render - Editors ########################
    renderEditor_Code = () => {
        const { editable } = this.state;
        const code = editable.item.code ?? '—';
        return _jsx(TS_PropRenderer.Vertical, { label: 'Code', children: _jsx("div", { className: 'editor__value code', onClick: () => ModuleFE_Thunderstorm.copyToClipboard(code), children: code }) });
    };
    renderEditor_RefSystem = () => {
        const { editable } = this.state;
        const refSystem = editable.item.refSystem;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Reference System', children: _jsx("div", { className: 'editor__value badge ref-system', children: refSystem?.toUpperCase() ?? '—' }) });
    };
    renderEditor_Text = () => {
        const { editable } = this.state;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Text', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("p", { className: 'editor__value', children: editable.item.text })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(TS_InputV2, { type: 'text', value: editable.item.text ?? '', onChange: value => this.onInputChange('text', value), className: 'editor__input', placeholder: 'Enter description text' })
                    }
                ] }) });
    };
    renderEditor_Display = () => {
        const { editable, editMode } = this.state;
        const display = editable.item.display;
        if (!display && !editMode)
            return;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Display', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("p", { className: 'editor__value', children: display || '—' })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(TS_InputV2, { type: 'text', value: display ?? '', onChange: value => this.onInputChange('display', value || undefined), className: 'editor__input', placeholder: 'Display Name (Optional)' })
                    }
                ] }) });
    };
    renderEditor_Status = () => {
        const { editable } = this.state;
        const status = editable.item.status;
        const statusAdapter = SimpleListAdapter([...UnresolvedCodeStatuses], item => _jsx("div", { children: StatusLabels[item.item] }));
        return _jsx(TS_PropRenderer.Vertical, { label: 'Status', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("div", { className: `editor__value badge status ${status ?? ''}`, children: status ? StatusLabels[status] : '—' })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(TS_DropDown, { adapter: statusAdapter, selected: status, onSelected: selected => {
                                editable.update('status', selected);
                                this.forceUpdate();
                            } })
                    }
                ] }) });
    };
    renderEditor_FhirQueryLabel = () => {
        const fhirQueryLabel = this.state.editable.item.fhirQueryLabel;
        return _jsx(TS_PropRenderer.Vertical, { label: 'FHIR Query', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("div", { className: 'editor__value', children: fhirQueryLabel })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(TS_InputV2, { type: 'text', value: fhirQueryLabel ?? '', onChange: value => this.onInputChange('fhirQueryLabel', value), className: 'editor__input', placeholder: 'Enter FHIR query label' })
                    }
                ] }) });
    };
    renderEditor_SuggestedHvIds = () => {
        const editMode = this.state.editMode;
        const { suggestedHvIds, chosenHvId } = this.state.editable.item;
        if (!suggestedHvIds || suggestedHvIds.length === 0)
            return;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Suggested HCS Variables', children: _jsx("div", { className: 'editor__chip-list', children: suggestedHvIds.map((id) => {
                    const variableName = ModuleFE_Variable.cache.unique(id)?.name ?? id;
                    const isSelected = chosenHvId === id;
                    return _jsx("span", { className: `editor__chip ${isSelected ? 'selected' : ''} ${editMode ? 'clickable' : ''}`, onClick: editMode ? () => this.onSelectVariable(id) : undefined, children: variableName }, id);
                }) }) });
    };
    renderEditor_ChosenHvId = () => {
        const editable = this.state.editable;
        const chosenHvId = editable.item.chosenHvId;
        return _jsx(TS_PropRenderer.Vertical, { label: 'HCS Variable', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("div", { className: 'editor__value', children: chosenHvId
                                ? ModuleFE_Variable.cache.unique(chosenHvId)?.name ?? 'Variable not found'
                                : 'No HCS Variable Selected' })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsxs(LL_H_C, { className: 'editor__hcs-variable-edit', children: [_jsx(ReactiveDropDown_VariableV3WithSynonyms.editable, { editable: editable, prop: 'chosenHvId', sortBy: ['_nameInLowercase'], onSelected: async (item, onSelected) => {
                                        await onSelected(item);
                                        this.forceUpdate();
                                    } }, editable.item._id), editable.item.chosenHvId && _jsx(ICONS.edit.component, { className: 'edit-variable-button', onClick: () => this.openVariableEditor(editable.item.chosenHvId), ...openContent.tooltip.top('edit-variable-tooltip', () => 'Edit variable') }), _jsx("span", { className: 'editor__or-separator', children: "OR" }), _jsx(Button, { className: 'create-variable', variant: 'secondary', onClick: () => this.openVariableEditor(), children: "Create New Variable" })] })
                    }
                ] }) });
    };
    renderEditor_KbVersion = () => {
        const { editable, editMode } = this.state;
        if (!editable.item.kbVersion && !editMode)
            return;
        const artifacts = ModuleFE_Artifact.cache.allMutable();
        const selectedArtifact = artifacts.find(artifact => artifact._id === editable.item.kbVersion);
        return _jsx(TS_PropRenderer.Vertical, { label: 'KB Version', children: _jsx("div", { className: 'editor__value id', children: selectedArtifact?.dataVersion ?? '—' }) });
    };
    renderRejectButton = () => {
        const { editMode, editable } = this.state;
        const status = editable.item.status;
        if (!status || !editMode)
            return;
        const showRejectedStatuses = [UnresolvedCodeStatus_NewAndWaitingForNLP, UnresolvedCodeStatus_WaitingForUserDecision];
        if (!showRejectedStatuses.includes(status))
            return;
        return _jsx("div", { className: 'editor__reject-button', children: _jsx(Button, { className: 'reject', variant: 'secondary', onClick: this.onClickReject, children: "Reject This Code" }) });
    };
}
//# sourceMappingURL=Editor_UnresolvedCode.js.map